/**
 * Название события для человека.
 *
 * Одно место на всё приложение: список событий, уведомление в браузере и
 * выгрузка обязаны называть одно и то же одинаково. Раньше строка перевода
 * стояла в трёх местах, и подтипы тревоги там разошлись бы первыми.
 *
 * Про тревоги. Буксировка, снятие питания, глушилка и удар — не типы событий,
 * а подтипы тревоги: Traccar шлёт тип `alarm`, а что случилось, кладёт в
 * `attributes.alarm`, по одному событию на подтип. Читать только тип значит
 * показывать «тревога» и на эвакуатор, и на обрезанный провод.
 */

import type { Dict } from './i18n'

type Named = {
  type: string
  attributes?: Record<string, unknown> | null
}

export function eventText(event: Named, t: Dict): string {
  if (event.type === 'alarm') {
    // Запасное слово — сам тип: словарь может отстать от Traccar, и тогда
    // событие всё равно должно быть видно, пусть и непереведённым.
    const generic = t.eventTypes.alarm ?? event.type
    const subtype = event.attributes?.alarm
    if (typeof subtype !== 'string' || subtype === '') return generic
    // Незнакомый подтип показываем вместе с исходным словом: событие, которого
    // мы не ждали, — повод его увидеть, а не спрятать за общим «тревога».
    return t.alarms[subtype] ?? `${generic}: ${subtype}`
  }
  return t.eventTypes[event.type] ?? event.type
}

/** Одно срабатывание расписания: когда и что вышло. */
export type ScheduleRun = {
  at: string
  blocked: number
  released: number
  refused: number
  failed: number
}

/**
 * Сводка по срабатыванию расписания одной строкой.
 *
 * Нули не перечисляются: строка «заперто 3, отпущено 0, отказано 0, сбоев 0»
 * прячет единственное важное слово среди трёх пустых. Отказ показывается даже
 * в одиночку — именно тогда машина осталась незапертой.
 */
export function runText(run: ScheduleRun, t: Dict): string {
  const parts: string[] = []
  if (run.blocked) parts.push(`${t.runBlocked} ${run.blocked}`)
  if (run.released) parts.push(`${t.runReleased} ${run.released}`)
  if (run.refused) parts.push(`${t.runRefused} ${run.refused}`)
  if (run.failed) parts.push(`${t.runFailed} ${run.failed}`)
  return parts.join(' · ')
}
